import * as React from 'react';
import * as echarts from 'echarts/core';
import { BarChart, LineChart } from 'echarts/charts';
import {
    TitleComponent,
    TooltipComponent,
    GridComponent,
    LegendComponent,
} from 'echarts/components';
import { LabelLayout, UniversalTransition } from 'echarts/features';
import { SVGRenderer } from 'echarts/renderers';
import { format, parseISO } from 'date-fns';

import type {
    GridComponentOption,
    BarSeriesOption,
    LineSeriesOption,
    TitleComponentOption,
    TooltipComponentOption,
    LegendComponentOption,
    YAXisComponentOption,
} from 'echarts';

type ECOption = echarts.ComposeOption<
    | BarSeriesOption
    | LineSeriesOption
    | TitleComponentOption
    | TooltipComponentOption
    | LegendComponentOption
    | GridComponentOption
    | YAXisComponentOption
>;

echarts.use([
    TitleComponent,
    TooltipComponent,
    GridComponent,
    BarChart,
    LineChart,
    LegendComponent,
    LabelLayout,
    UniversalTransition,
    SVGRenderer,
]);

interface SessionTrend {
    period: string;
    period_start: string;
    total_sessions?: number;
    avg_session_duration_seconds?: number;
    avg_pages_per_session?: number;
    bounce_rate_percent?: number;
}

interface SessionMetricChartProps {
    data?: SessionTrend[];
    period: 'daily' | 'weekly' | 'monthly' | 'quarterly';
    metric: 'sessions' | 'avgPages' | 'avgDuration' | 'bounceRate';
    title: string;
    chartType?: 'bar' | 'line';
    color?: string;
    yAxisName?: string;
    formatter?: (value: number) => string;
    showTitle?: boolean;
}

const SessionMetricChart = ({ 
    data, 
    period, 
    metric, 
    title, 
    chartType = 'bar',
    color = '#1890ff',
    yAxisName,
    formatter,
    showTitle = true,
}: SessionMetricChartProps) => {
    const chartRef = React.useRef<HTMLDivElement>(null);
    const chartInstance = React.useRef<echarts.ECharts | null>(null);

    React.useEffect(() => {
        if (!data || data.length === 0) {
            if (chartInstance.current && chartRef.current) {
                chartInstance.current.setOption({ series: [] });
            }
            return;
        }

        if (chartRef.current) {
            if (!chartInstance.current) {
                chartInstance.current = echarts.init(chartRef.current);
            }

            const categories: string[] = [];
            const values: number[] = [];

            data.forEach(item => {
                let date: Date;
                try {
                    if (item.period_start) {
                        date = parseISO(item.period_start);
                        if (isNaN(date.getTime())) {
                            date = new Date(item.period_start);
                        }
                    } else {
                        date = new Date();
                    }
                } catch (e) {
                    date = new Date(item.period_start || new Date());
                }

                let label = '';
                switch (period) {
                    case 'daily':
                        label = format(date, 'MMM d');
                        break;
                    case 'weekly':
                        label = `W${format(date, 'II')} ${format(date, 'yyyy')}`;
                        break;
                    case 'monthly':
                        label = format(date, 'MMM yyyy');
                        break;
                    case 'quarterly':
                        label = `Q${Math.floor(date.getMonth() / 3) + 1} ${date.getFullYear()}`;
                        break;
                    default:
                        label = item.period || '';
                }

                categories.push(label);
                
                let value = 0;
                switch (metric) {
                    case 'sessions':
                        value = Number(item.total_sessions) || 0;
                        break;
                    case 'avgPages':
                        value = Number(item.avg_pages_per_session) || 0;
                        break;
                    case 'avgDuration':
                        value = Number(item.avg_session_duration_seconds) || 0;
                        break;
                    case 'bounceRate':
                        value = Number(item.bounce_rate_percent) || 0;
                        break;
                }
                values.push(value);
            });

            const defaultFormatter = (val: number) => {
                if (formatter) return formatter(val);
                if (metric === 'bounceRate') return `${val.toFixed(1)}%`;
                if (metric === 'avgDuration') return `${Math.round(val)}s`; // Round to whole seconds
                return val.toFixed(metric === 'avgPages' ? 2 : 0);
            };

            const option: ECOption = {
                ...(showTitle ? {
                    title: {
                        text: title,
                        left: 'center',
                        textStyle: {
                            fontSize: 14,
                            fontWeight: 'bold',
                        },
                    },
                } : {}),
                tooltip: {
                    trigger: 'axis',
                    axisPointer: { type: chartType === 'bar' ? 'shadow' : 'line' },
                    formatter: (params: any) => {
                        const idx = params[0]?.dataIndex ?? 0;
                        const val = values[idx];
                        return `${categories[idx]}<br/>${title}: ${defaultFormatter(val)}`;
                    },
                },
                grid: {
                    left: '10%',
                    right: '10%',
                    bottom: '15%',
                    top: showTitle ? '20%' : '12%',
                    containLabel: true,
                },
                xAxis: {
                    type: 'category',
                    data: categories,
                    axisLabel: {
                        rotate: period === 'daily' ? 45 : 0,
                        fontSize: 10,
                    },
                },
                yAxis: {
                    type: 'value',
                    name: yAxisName || title,
                    nameLocation: 'middle',
                    nameGap: 40,
                    nameTextStyle: {
                        fontSize: 11,
                    },
                    ...(metric === 'bounceRate' ? { min: 0, max: 100 } : {}),
                    axisLabel: {
                        formatter: (value: number) => {
                            if (formatter) {
                                // Use custom formatter but remove the 's' suffix for Y-axis
                                const formatted = formatter(value);
                                return formatted.replace(/s$/, '');
                            }
                            if (metric === 'bounceRate') {
                                return `${value.toFixed(0)}%`;
                            }
                            if (metric === 'avgDuration') {
                                return value.toFixed(0); // Show whole seconds on Y-axis
                            }
                            if (metric === 'avgPages') {
                                return value.toFixed(1);
                            }
                            return value.toFixed(0);
                        }
                    }
                },
                series: [
                    {
                        name: title,
                        type: chartType,
                        data: values,
                        itemStyle: { color },
                        ...(chartType === 'bar' ? { barMaxWidth: 40 } : {
                            smooth: true,
                            symbol: 'circle',
                            symbolSize: 6,
                            lineStyle: { color, width: 2 },
                        }),
                    },
                ],
            };

            chartInstance.current.setOption(option);
        }

        const resizeHandler = () => {
            chartInstance.current?.resize();
        };
        window.addEventListener('resize', resizeHandler);

        return () => {
            window.removeEventListener('resize', resizeHandler);
            chartInstance.current?.dispose();
            chartInstance.current = null;
        };
    }, [data, period, metric, title, chartType, color, yAxisName, formatter, showTitle]);

    return <div ref={chartRef} style={{ width: '100%', height: 300 }} />;
};

export default SessionMetricChart;
